import Vue from "vue";
import ModernFaceMesh, {
  FACE_LANDMARKER_ERROR_CODES,
  FaceLandmarkerError,
  classifyFaceLandmarkerError,
} from "./modernFaceMesh";

export const FACE_LANDMARKER_STATUS = Object.freeze({
  IDLE: "idle",
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
});

const TOTAL_INITIALIZATION_TIMEOUT_MS = 50000;
const GPU_INITIALIZATION_TIMEOUT_MS = 20000;

function now() {
  return typeof performance !== "undefined" && performance.now
    ? performance.now()
    : Date.now();
}

function publicError(error) {
  const normalized = classifyFaceLandmarkerError(error);
  return {
    code: normalized.code,
    message: normalized.message,
    phase: normalized.phase || "initialization",
    delegate: normalized.delegate || null,
  };
}

class FaceLandmarkerManager {
  constructor() {
    this.state = Vue.observable({
      status: FACE_LANDMARKER_STATUS.IDLE,
      error: null,
      delegate: null,
      recoveryStatus: "idle",
      generation: 0,
      initializationTimeMs: null,
    });

    this._generation = 0;
    this._instance = null;
    this._activeCandidate = null;
    this._initializationPromise = null;
    this._disposed = false;
  }

  getInstance() {
    return this.state.status === FACE_LANDMARKER_STATUS.READY
      ? this._instance
      : null;
  }

  ensureReady() {
    if (
      this.state.status === FACE_LANDMARKER_STATUS.READY &&
      this._instance
    ) {
      return Promise.resolve(this._instance);
    }

    if (
      this.state.status === FACE_LANDMARKER_STATUS.LOADING &&
      this._initializationPromise
    ) {
      return this._initializationPromise;
    }

    if (this.state.status === FACE_LANDMARKER_STATUS.ERROR) {
      const error = this.state.error || {};
      return Promise.reject(
        new FaceLandmarkerError(
          error.code || FACE_LANDMARKER_ERROR_CODES.INIT_FAILED,
          error.message || "FaceLandmarker initialization failed",
          {
            phase: error.phase,
            delegate: error.delegate,
          }
        )
      );
    }

    this._disposed = false;
    const generation = ++this._generation;
    const startedAt = now();

    this.state.status = FACE_LANDMARKER_STATUS.LOADING;
    this.state.error = null;
    this.state.delegate = null;
    this.state.recoveryStatus = "idle";
    this.state.generation = generation;
    this.state.initializationTimeMs = null;

    const initializationPromise = this._initializeGeneration(
      generation,
      startedAt
    );

    this._initializationPromise = initializationPromise;
    return initializationPromise;
  }

  retry() {
    this._invalidateGeneration();
    this._disposeInstances();
    this._initializationPromise = null;
    this._disposed = false;

    this.state.status = FACE_LANDMARKER_STATUS.IDLE;
    this.state.error = null;
    this.state.delegate = null;
    this.state.recoveryStatus = "idle";
    this.state.initializationTimeMs = null;

    return this.ensureReady();
  }

  dispose() {
    this._disposed = true;
    this._invalidateGeneration();
    this._disposeInstances();
    this._initializationPromise = null;

    this.state.status = FACE_LANDMARKER_STATUS.IDLE;
    this.state.error = null;
    this.state.delegate = null;
    this.state.recoveryStatus = "idle";
    this.state.generation = this._generation;
    this.state.initializationTimeMs = null;
  }

  async _initializeGeneration(generation, startedAt) {
    let gpuError = null;

    try {
      this._assertGeneration(generation);

      try {
        const gpuBudget = Math.min(
          GPU_INITIALIZATION_TIMEOUT_MS,
          this._remainingBudget(startedAt)
        );
        const gpuInstance = await this._initializeDelegate(
          "GPU",
          gpuBudget,
          generation
        );
        return this._commitReady(gpuInstance, generation, startedAt);
      } catch (error) {
        gpuError = classifyFaceLandmarkerError(error, {
          phase: "initialization",
          delegate: "GPU",
        });

        if (
          gpuError.code === FACE_LANDMARKER_ERROR_CODES.UNSUPPORTED_BROWSER ||
          gpuError.code === FACE_LANDMARKER_ERROR_CODES.ASSET_LOAD_FAILED ||
          gpuError.code === FACE_LANDMARKER_ERROR_CODES.CANCELLED
        ) {
          throw gpuError;
        }

        console.warn(
          "FaceLandmarker GPU initialization failed; falling back to CPU",
          publicError(gpuError)
        );
      }

      this._assertGeneration(generation);
      const cpuBudget = this._remainingBudget(startedAt);
      if (cpuBudget <= 0) {
        throw new FaceLandmarkerError(
          FACE_LANDMARKER_ERROR_CODES.INIT_TIMEOUT,
          "FaceLandmarker initialization timed out",
          { phase: "initialization", delegate: "CPU" }
        );
      }

      const cpuInstance = await this._initializeDelegate(
        "CPU",
        cpuBudget,
        generation
      );
      return this._commitReady(cpuInstance, generation, startedAt);
    } catch (error) {
      const normalized = classifyFaceLandmarkerError(error, {
        phase: "initialization",
      });

      if (gpuError && !normalized.gpuError) {
        normalized.gpuError = publicError(gpuError);
      }

      if (this._isCurrentGeneration(generation)) {
        this._instance = null;
        this.state.status = FACE_LANDMARKER_STATUS.ERROR;
        this.state.error = publicError(normalized);
        this.state.delegate = null;
        this.state.initializationTimeMs = Math.round(now() - startedAt);
      }

      throw normalized;
    } finally {
      if (this._isCurrentGeneration(generation)) {
        this._activeCandidate = null;
        this._initializationPromise = null;
      }
    }
  }

  async _initializeDelegate(delegate, timeoutMs, generation) {
    this._assertGeneration(generation);

    const candidate = new ModernFaceMesh({
      onDelegateChange: (nextDelegate) => {
        if (
          this._isCurrentGeneration(generation) &&
          this._instance === candidate
        ) {
          this.state.delegate = nextDelegate;
        }
      },
      onRuntimeRecoveryState: (recoveryStatus) => {
        if (
          this._isCurrentGeneration(generation) &&
          this._instance === candidate
        ) {
          this.state.recoveryStatus = recoveryStatus;
        }
      },
      onRuntimeError: (error) => {
        if (
          this._isCurrentGeneration(generation) &&
          this._instance === candidate
        ) {
          // Remove the unusable instance immediately, but defer close until the
          // current send() stack has unwound. Some MediaPipe implementations
          // throw if their graph is closed from inside an inference callback.
          this._instance = null;
          this.state.status = FACE_LANDMARKER_STATUS.ERROR;
          this.state.error = publicError(error);
          this.state.delegate = candidate.delegate || null;
          this._initializationPromise = null;
          Promise.resolve().then(() => {
            try {
              candidate.close();
            } catch (closeError) {
              console.warn(
                "Failed to dispose FaceLandmarker after a runtime error",
                closeError
              );
            }
          });
        }
      },
    });

    candidate.setOptions({
      maxNumFaces: 1,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });

    this._activeCandidate = candidate;

    try {
      await candidate.initialize({ delegate, timeoutMs });
      this._assertGeneration(generation);
      return candidate;
    } catch (error) {
      candidate.close();
      if (this._activeCandidate === candidate) {
        this._activeCandidate = null;
      }
      throw error;
    }
  }

  _commitReady(instance, generation, startedAt) {
    this._assertGeneration(generation);

    if (this._instance && this._instance !== instance) {
      this._instance.close();
    }

    this._instance = instance;
    this._activeCandidate = null;
    this.state.delegate = instance.delegate;
    this.state.error = null;
    this.state.recoveryStatus = "idle";
    this.state.initializationTimeMs = Math.round(now() - startedAt);
    this.state.status = FACE_LANDMARKER_STATUS.READY;

    return instance;
  }

  _remainingBudget(startedAt) {
    return Math.max(
      0,
      TOTAL_INITIALIZATION_TIMEOUT_MS - (now() - startedAt)
    );
  }

  _assertGeneration(generation) {
    if (!this._isCurrentGeneration(generation)) {
      throw new FaceLandmarkerError(
        FACE_LANDMARKER_ERROR_CODES.CANCELLED,
        "FaceLandmarker initialization was cancelled",
        { phase: "initialization" }
      );
    }
  }

  _isCurrentGeneration(generation) {
    return !this._disposed && generation === this._generation;
  }

  _invalidateGeneration() {
    this._generation += 1;
    this.state.generation = this._generation;
  }

  _disposeInstances() {
    if (this._activeCandidate) {
      this._activeCandidate.close();
      this._activeCandidate = null;
    }
    if (this._instance) {
      this._instance.close();
      this._instance = null;
    }
  }
}

const faceLandmarkerManager = new FaceLandmarkerManager();

export { FaceLandmarkerManager };
export default faceLandmarkerManager;
